/**
 * VixChart — India VIX line over the backtest period with regime bands.
 *
 * Bands:
 *   < 15  calm
 *   15–20 normal
 *   20–25 elevated  (light amber)
 *   > 25  hostile   (light red)
 *
 * Props:
 *   data    [{date, vix}]
 *   height  number  SVG height (default 160)
 */
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";

const BAND_ELEVATED = 20;
const BAND_HOSTILE  = 25;

export default function VixChart({ data = [], height = 160 }) {
  if (!data.length) return null;

  const values  = data.map(d => d.vix);
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);

  // Pad the y-range a touch, and always include the elevated threshold so
  // the regime bands are visible even when VIX stays low for the period.
  const minV = Math.max(0, Math.min(dataMin, 12) - 1);
  const maxV = Math.max(dataMax, BAND_HOSTILE + 2);

  // Mean for context
  const mean = values.reduce((a, b) => a + b, 0) / values.length;

  const chartData = data.map(d => ({ date: d.date, VIX: d.vix }));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-4 text-xs">
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Mean</em>
          <strong className="text-foreground">{mean.toFixed(1)}</strong>
        </span>
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Min</em>
          <strong className="text-foreground">{dataMin.toFixed(1)}</strong>
        </span>
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Max</em>
          <strong className="text-loss">{dataMax.toFixed(1)}</strong>
        </span>
        <span className="flex flex-col">
          <em className="text-[10px] uppercase font-semibold text-muted-foreground not-italic mb-0.5">Days &gt; 20</em>
          <strong className="text-foreground">
            {values.filter(v => v > BAND_ELEVATED).length}
          </strong>
        </span>
      </div>

      <div className="w-full text-xs" style={{ height: `${height}px` }}>
        <ResponsiveContainer>
          <ComposedChart
            data={chartData}
            margin={{ top: 10, right: 0, left: -25, bottom: 0 }}
          >
            <CartesianGrid
              strokeWidth={1} strokeOpacity={0.5}
              strokeDasharray="3 3" vertical={false}
            />

            <XAxis
              dataKey="date"
              axisLine={false}
              dy={10}
              tick={{ fill: 'hsl(var(--foreground))' }}
              minTickGap={10}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[minV, maxV]}
              tick={{ fill: 'hsl(var(--foreground))' }}
            />

            <Tooltip
              labelStyle={{ color: 'hsl(var(--foreground))' }}
              contentStyle={{ background: 'hsl(var(--background))', borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
            />

            {/* Regime bands */}
            <ReferenceArea y1={BAND_ELEVATED} y2={BAND_HOSTILE} fill="#f59e0b" fillOpacity={0.08} ifOverflow="hidden" />
            <ReferenceArea y1={BAND_HOSTILE} y2={maxV} fill="#f87171" fillOpacity={0.1} ifOverflow="hidden" />

            {/* Threshold reference lines */}
            <ReferenceLine y={BAND_ELEVATED} stroke="#f59e0b" strokeDasharray="3 3" strokeOpacity={0.5} />
            <ReferenceLine y={BAND_HOSTILE} stroke="#f87171" strokeDasharray="3 3" strokeOpacity={0.5} />

            <Line
              type="monotone"
              dataKey="VIX"
              stroke="#a78bfa"
              strokeWidth={1.5}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
